'use client'
import React, { FC, PropsWithChildren, createContext, useState, useCallback } from 'react'
import axios from 'axios'
import Wallet from '@/utils/wallet'
import { INSCRIBE_API_URL, NETWORK_CONFIG } from '@/utils/constants'
import useDebouncedCallback from '@/utils/hooks/useDebouncedCallback'
import { UploadContext } from '@/app/inscribe/inscribe/context'

export const SEARCH_COLLECTION = 0
export const CONFIGURE_GALLERY = 1
export const INSCRIBE_GALLERY = 2
export const COMPLETE = 3

export interface GalleryItem {
	inscriptionId: string
	title: string
	traits: Record<string, string | number | boolean>
}

export const GalleryContext = createContext<any>(null)

const POSTAGE = (NETWORK_CONFIG as any).postage

const GalleryProvider: FC<PropsWithChildren> = (props) => {
	const [step, setStep] = useState(SEARCH_COLLECTION)
	const [collection, setCollection] = useState<any>(null)
	const [feeRate, setFeeRate] = useState(10.0)
	const [minFeeRate, setMinFeeRate] = useState(5)
	const [postage, setPostage] = useState(POSTAGE)
	const [parentInscriptionId, setParentInscriptionId] = useState('')
	const [parentOutpoint, setParentOutpoint] = useState('')
	const [parentUtxoValue, setParentUtxoValue] = useState(POSTAGE)
	const [cost, setCost] = useState(0)
	const [estimateData, setEstimateData] = useState<any>(null)
	const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([])
	const [galleryFile, setGalleryFile] = useState<File | null>(null)
	const [delegateInscriptionId, setDelegateInscriptionId] = useState('')
	const [contentMode, setContentMode] = useState<'file' | 'delegate'>('delegate')
	const [includeTraits, setIncludeTraits] = useState(true)
	const [includeTitles, setIncludeTitles] = useState(true)
	const [metaName, setMetaName] = useState('')
	const [metaDescription, setMetaDescription] = useState('')
	const [metaWebsite, setMetaWebsite] = useState('')
	const [metaTwitter, setMetaTwitter] = useState('')
	const [metaDiscord, setMetaDiscord] = useState('')
	const [propertiesJsonOverride, setPropertiesJsonOverride] = useState<string | null>(null)
	const [sourceMode, setSourceMode] = useState<'collection' | 'json'>('collection')
	const [txid, setTxid] = useState('')

	const buildPropertiesJson = useCallback(() => {
		if (propertiesJsonOverride !== null) {
			try {
				return JSON.parse(propertiesJsonOverride)
			} catch {
				// Fall through to default build if invalid JSON
			}
		}

		const properties: Record<string, any> = {
			'0': galleryItems.map((item) => {
				const attrs: Record<string, any> = {}
				if (includeTitles && item.title) attrs['0'] = item.title
				if (includeTraits && Object.keys(item.traits).length > 0) attrs['1'] = item.traits
				return {
					'0': item.inscriptionId,
					...(Object.keys(attrs).length > 0 ? { '1': attrs } : {})
				}
			})
		}

		// Collection metadata — ord Attributes format: { 0: title, 1: traits_map }
		if (metaName) {
			const traits: Record<string, any> = {}
			// TODO: Re-enable parent descriptions once tested
			// if (metaDescription) traits.description = metaDescription
			if (metaWebsite) traits.website = metaWebsite
			if (metaTwitter) traits.twitter = metaTwitter
			if (metaDiscord) traits.discord = metaDiscord
			if (collection?.slug) traits.slug = collection.slug

			properties['1'] = {
				'0': metaName,
				...(Object.keys(traits).length > 0 ? { '1': traits } : {})
			}
		}

		return properties
	}, [
		propertiesJsonOverride, galleryItems, includeTitles, includeTraits,
		metaName, metaWebsite, metaTwitter, metaDiscord, collection
	])

	const buildMetadataJson = () => {
		const meta: Record<string, any> = {}
		// TODO: Re-enable parent descriptions once tested
		// if (metaDescription) meta.description = metaDescription
		return Object.keys(meta).length > 0 ? meta : undefined
	}

	const requestEstimate = useCallback(async () => {
		if (!feeRate) return

		const hasContent = contentMode === 'file' ? !!galleryFile : !!delegateInscriptionId
		if (!hasContent) return

		try {
			const properties = galleryItems.length > 0 ? buildPropertiesJson() : undefined
			const fileSize = contentMode === 'file' ? (galleryFile as any).size : 1
			const contentType =
				contentMode === 'file'
					? (galleryFile as any).type || 'application/octet-stream'
					: 'application/octet-stream'

			// For large properties, gzip + base64 encode to keep the request
			// payload small while giving the server exact data for accurate estimates.
			let estimateProperties: any = undefined
			let propertiesGz: string | undefined = undefined
			if (properties) {
				const jsonStr = JSON.stringify(properties)
				const jsonBytes = new TextEncoder().encode(jsonStr).length
				if (jsonBytes > 100_000) {
					const stream = new Blob([jsonStr]).stream()
					const compressed = stream.pipeThrough(new CompressionStream('gzip'))
					const buf = await new Response(compressed).arrayBuffer()
					const bytes = new Uint8Array(buf)
					let binary = ''
					for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
					propertiesGz = btoa(binary)
				} else {
					estimateProperties = properties
				}
			}

			const { data } = await axios.post(`${INSCRIBE_API_URL}/inscribe/estimate`, {
				destination: Wallet.getTaprootAddress(),
				fee_rate: feeRate,
				postage: postage,
				file_size: fileSize,
				content_type: contentType,
				parent_inscription_id: parentInscriptionId || undefined,
				properties: estimateProperties,
				properties_gz: propertiesGz
			})
			setEstimateData(data)
			setCost(data.total_cost)
		} catch (e) {
			console.log('Estimate error', e)
		}
	}, [
		feeRate, contentMode, galleryFile, delegateInscriptionId, galleryItems.length,
		buildPropertiesJson, postage, parentInscriptionId
	])
	const estimate = useDebouncedCallback(requestEstimate, 100)

	// UploadContext compatibility for FeeSlider component
	const uploadContextValue = {
		estimate,
		feeRate,
		setFeeRate,
		fileSize: galleryFile?.size || 1,
		setCost,
		setMinFeeRate,
		minFeeRate,
		step,
		setStep,
		files: galleryFile ? [{ file: galleryFile }] : [],
		setFiles: () => {},
		cost,
		estimateData,
		postage,
		setPostage
	}

	return (
		<GalleryContext.Provider
			value={{
				step,
				setStep,
				collection,
				setCollection,
				feeRate,
				setFeeRate,
				minFeeRate,
				setMinFeeRate,
				postage,
				setPostage,
				parentInscriptionId,
				setParentInscriptionId,
				parentOutpoint,
				setParentOutpoint,
				parentUtxoValue,
				setParentUtxoValue,
				cost,
				setCost,
				estimateData,
				setEstimateData,
				estimate,
				galleryItems,
				setGalleryItems,
				galleryFile,
				setGalleryFile,
				delegateInscriptionId,
				setDelegateInscriptionId,
				contentMode,
				setContentMode,
				includeTraits,
				setIncludeTraits,
				includeTitles,
				setIncludeTitles,
				metaName,
				setMetaName,
				metaDescription,
				setMetaDescription,
				metaWebsite,
				setMetaWebsite,
				metaTwitter,
				setMetaTwitter,
				metaDiscord,
				setMetaDiscord,
				propertiesJsonOverride,
				setPropertiesJsonOverride,
				sourceMode,
				setSourceMode,
				txid,
				setTxid,
				buildPropertiesJson,
				buildMetadataJson
			}}
		>
			<UploadContext.Provider value={uploadContextValue}>
				{props.children}
			</UploadContext.Provider>
		</GalleryContext.Provider>
	)
}

export default GalleryProvider
